import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import CommonAlert from "@/components/CommonAlert";
import useHotelClinicTasksBulkMutation from "@/providers/hooks/useHotelClinicTasksBulkMutation";
import {
    ClinicArrivalTasksBulkConfirmTypes,
    useClinicArrivalTasksBulkConfirm,
    useClinicArrivalTasksGridActions,
} from "@/stores/use-clinic-arrival-tasks-grid";

/** Confirms bulk clinic tasks PATCH before submit (multi-row status change or delete). */
function ClinicArrivalTasksBulkConfirmDialog() {
    const bulkConfirm = useClinicArrivalTasksBulkConfirm();
    const { t } = useTranslation();
    const { updateBulkTasksConfirm } = useClinicArrivalTasksGridActions();
    const { handleHotelClinicTasksBulkMutation, isPending } = useHotelClinicTasksBulkMutation();

    const open = !!bulkConfirm && bulkConfirm.tasks.length > 0;
    const isDeleteConfirm = bulkConfirm?.type === ClinicArrivalTasksBulkConfirmTypes.DELETE;

    const handleConfirm = async () => {
        if (isPending || !bulkConfirm?.tasks.length) return;
        await handleHotelClinicTasksBulkMutation({ tasks: bulkConfirm.tasks });
        updateBulkTasksConfirm(null);
    };

    const handleClose = () => {
        updateBulkTasksConfirm(null);
    };

    const descriptionOpts = useMemo((): Record<string, string | number> => {
        if (!open || !bulkConfirm) return { count: 0 };

        if (bulkConfirm.type === ClinicArrivalTasksBulkConfirmTypes.DELETE) {
            return { count: bulkConfirm.tasks.length };
        }

        const targetStatus = bulkConfirm.tasks[0]?.updateData.status ?? "";
        return {
            count: bulkConfirm.tasks.length,
            targetStatus: t(`hotelClinicStatuses.${targetStatus}`),
        };
    }, [bulkConfirm, open, t]);

    const descriptionKey =
        bulkConfirm?.type === ClinicArrivalTasksBulkConfirmTypes.DELETE
            ? "confirmations.clinicArrivalTaskDelete"
            : "confirmations.clinicArrivalTaskBulkStatus";

    return (
        <CommonAlert
            open={open}
            loading={isPending}
            description={descriptionKey}
            color={isDeleteConfirm ? "error" : "primary"}
            onClose={handleClose}
            handleConfirm={handleConfirm}
            confirmText={isDeleteConfirm ? "buttons.delete" : undefined}
            descriptionOpts={descriptionOpts}
            html
        />
    );
}

export default ClinicArrivalTasksBulkConfirmDialog;
